import { useQuery } from '@apollo/react-hooks'
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Grid,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Link as RouterLink } from '@reach/router'
import { gql } from 'apollo-boost'
import ErrorMessage from 'components/ErrorMessage'
import ResultsTablePagination from 'components/ResultsTablePagination'
import SkeletonTable from 'components/SkeletonTable'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import constants from 'utils/constants'
import { useQueryParams } from 'utils/hooks'
import { getDateMinAndMaxFromPeriod } from 'utils/period'
import getInitials from 'utils/getInitials'
import qs from 'qs'
import { jsonToCsv, jsonToXlsx } from 'utils/exportUtils'
import ExportMenuList from 'components/ExportMenuList'
import fileDownload from 'js-file-download'

const GET_SCHOOLS = gql`
  query ($filter: SchoolFilterInput!) {
    schoolsSearch(filter: $filter) {
      items {
        id
        slug
        name
        email
      }
      total
    }
  }
`

const GET_LOGS = gql`
  query logsSearch($filter: LogFilterInput!) {
    logsSearch(filter: $filter) {
      items {
        id
        type
        contentRaw
        createdAt
        school {
          id
        }
      }
    }
  }
`

const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(3) },
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 700,
  },
  cell: {
    whiteSpace: 'nowrap',
  },
  link: {
    display: 'block',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}))

const aggregate = (school, logs) => {
  const schoolLogs = logs.filter((l) => l.school?.id === school.id)
  return {
    ...school,
    numberOfMessages: schoolLogs.filter((l) => l.type !== constants.logTypes.system).length,
    numberOfLabelUpdates: schoolLogs.filter(
      (l) =>
        l.type === constants.logTypes.system && l.contentRaw.includes('oppdaterte etikettene')
    ).length,
    numberOfEnrollmentUpdates: schoolLogs.filter(
      (l) =>
        l.type === constants.logTypes.system && l.contentRaw.includes('oppdaterte påmeldingen')
    ).length,
  }
}

const Results = (props) => {
  const { location } = props
  const { period, dateMin, dateMax } = qs.parse(location.search, { ignoreQueryPrefix: true }) || {}
  const classes = useStyles()
  const { queryVariables, page, searchString, rowsPerPage } = useQueryParams(location)
  const [schoolIds, setSchoolIds] = useState(false)
  const [queryPeriod, setQueryPeriod] = useState(0)
  const [limits, setLimits] = useState(null)
  const [loadingExport, setLoadingExport] = useState(false)
  const [exportPage, setExportPage] = useState(1)

  const {
    data: schoolsData,
    loading: schoolsLoading,
    error: schoolsError,
    client,
  } = useQuery(GET_SCHOOLS, {
    variables: { filter: { ...queryVariables } },
  })

  useEffect(() => {
    if (!schoolsLoading && !schoolsError && schoolsData?.schoolsSearch?.items) {
      const ids = schoolsData?.schoolsSearch?.items
        .map((s) => s.id)
        .filter((id, i, ids) => ids.indexOf(id) === i)

      if (ids !== schoolIds) {
        setSchoolIds(ids)
      }
    } else {
      setSchoolIds(null)
    }
    if (period !== queryPeriod) {
      setQueryPeriod(period || 0)
      setLimits(getDateMinAndMaxFromPeriod(period || 0))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolsLoading, schoolsError, schoolsData, period])

  const {
    data: logsData,
    loading: logsLoading,
    error: logsError,
  } = useQuery(GET_LOGS, {
    variables: {
      filter: {
        schools: schoolIds,
        types: [constants.logTypes.call, constants.logTypes.message, constants.logTypes.system],
        dateMin: dateMin || limits?.dateMin,
        dateMax: dateMax || limits?.dateMax,
        rowsPerPage: 100000 /* all matches to fit on one page */,
      },
    },
    skip: !schoolIds,
    fetchPolicy: 'no-cache',
  })

  if (schoolsError || logsError)
    return (
      <ErrorMessage>{(schoolsError && schoolsError.message) || logsError.message}</ErrorMessage>
    )

  if (schoolsLoading || logsLoading || !schoolsData?.schoolsSearch || !logsData?.logsSearch)
    return (
      <SkeletonTable
        cardHeader={'Alle skoler'}
        searchString={searchString}
        rowsPerPage={rowsPerPage}
        columns={constants.columns.schoolsActivities}
      />
    )

  const { items: schools, total } = schoolsData?.schoolsSearch
  const { items: logs } = logsData?.logsSearch

  const items = schools.map((s) => aggregate(s, logs))

  const cardTitle = `${
    searchString === '' ? 'Alle skoler' : `Søk på ${searchString}`
  }, periode: ${new Date(dateMin || limits?.dateMin)?.toLocaleDateString()} - ${new Date(
    dateMax || limits?.dateMax
  )?.toLocaleDateString()}`

  const handleExport = async (exportType) => {
    setLoadingExport(true)

    let items = []
    for (let i = 0; i < Math.ceil(total / rowsPerPage); i++) {
      const schoolsResponse = await client.query({
        query: GET_SCHOOLS,
        variables: {
          filter: {
            ...queryVariables,
            page: i,
            rowsPerPage,
            sort: {
              order: 'asc',
              property: 'name',
            },
          },
        },
        fetchPolicy: 'no-cache',
      })

      const pageSchools = schoolsResponse?.data?.schoolsSearch?.items
      const pageSchoolIds = pageSchools
        .map((s) => s.id)
        .filter((id, i, ids) => ids.indexOf(id) === i)

      const logsResponse = await client.query({
        query: GET_LOGS,
        variables: {
          filter: {
            schools: pageSchoolIds,
            types: [constants.logTypes.call, constants.logTypes.message, constants.logTypes.system],
            dateMin: dateMin || limits?.dateMin,
            dateMax: dateMax || limits?.dateMax,
            rowsPerPage: 100000,
          },
        },
        fetchPolicy: 'no-cache',
      })

      const pageLogs = logsResponse?.data?.logsSearch?.items
      const pageItems = pageSchools.map((s) => aggregate(s, pageLogs))
      if (pageItems) {
        items = items.concat(pageItems)
      }

      setExportPage(i + 1)
    }

    if (exportType === constants.exportTypes.csv) {
      const columns = [
        'name',
        'email',
        'numberOfMessages',
        'numberOfLabelUpdates',
        'numberOfEnrollmentUpdates',
      ]
      const csv = await jsonToCsv(items, columns)
      fileDownload(csv, 'aktivitet-skoler.csv', 'text/csv')
    } else if (exportType === constants.exportTypes.xls) {
      const columns = [
        { label: 'name', value: 'name' },
        { label: 'email', value: 'email' },
        { label: 'numberOfMessages', value: 'numberOfMessages' },
        { label: 'numberOfLabelUpdates', value: 'numberOfLabelUpdates' },
        { label: 'numberOfEnrollmentUpdates', value: 'numberOfEnrollmentUpdates' },
      ]

      const settings = {
        sheetName: 'aktivitet-skoler',
        fileName: 'aktivitet-skoler',
        extraLength: 3,
        writeOptions: {},
      }

      jsonToXlsx(columns, items, settings, true)
    }

    setLoadingExport(false)
    setExportPage(1)
  }

  return (
    <div className={classes.root}>
      <Typography color="textSecondary" gutterBottom variant="body2">
        {total} Resultater. Side {page + 1} av {Math.ceil(total / rowsPerPage)}
      </Typography>
      <Card>
        <CardHeader
          title={cardTitle}
          action={
            <Grid
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="stretch"
              spacing={2}
            >
              <Grid item>
                <Box visibility={loadingExport ? 'visible' : 'hidden'} className={classes.box}>
                  <Typography color="textSecondary" variant="body2">
                    Lastet ned {exportPage} av {Math.ceil(total / rowsPerPage)} side
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <ExportMenuList
                  onSelect={handleExport}
                  loadingExport={loadingExport}
                ></ExportMenuList>
              </Grid>
            </Grid>
          }
        />
        <Divider />
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    {constants.columns.schoolsActivities.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    return (
                      <TableRow hover key={item.id}>
                        <TableCell className={classes.cell}>
                          <div className={classes.nameCell}>
                            <Avatar className={classes.avatar}>{getInitials(item.name)}</Avatar>
                            <div>
                              <Link
                                color="inherit"
                                component={RouterLink}
                                to={`/schools/${item.slug}`}
                                variant="h6"
                              >
                                {item.name}
                              </Link>
                              {item.email && (
                                <Link
                                  color="primary"
                                  className={classes.link}
                                  href={`mailto:${item.email}`}
                                >
                                  {item.email}
                                </Link>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className={classes.cell}>{item.numberOfMessages}</TableCell>
                        <TableCell className={classes.cell}>{item.numberOfLabelUpdates}</TableCell>
                        <TableCell className={classes.cell}>
                          {item.numberOfEnrollmentUpdates}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
        <CardActions className={classes.actions}>
          <ResultsTablePagination
            location={location}
            page={page}
            total={total}
            rowsPerPage={rowsPerPage}
            queryVariables={queryVariables}
            client={client}
            query={GET_SCHOOLS}
          />
        </CardActions>
      </Card>
    </div>
  )
}

Results.propTypes = {
  location: PropTypes.object,
}

Results.defaultProps = {}

export default Results
